// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'Hooks';
import { clearFlash } from 'Features/fileSelection/fileSelectionSlice';

// Width/opacity steps for the ~1.2s double-click zoom pulse. Mapbox tweens
// paint changes with its built-in 300ms transition, so four steps read as two
// smooth throbs that fade out.
const FLASH_STEPS: Array<[number, number]> = [[10, 1], [3, 0.9], [9, 0.8], [2, 0]];
const FLASH_STEP_MS = 300;

interface IFootprintFlashFrame {
  kind: FileSelectionKind;
  id: string;
  width: number;
  opacity: number;
}

// Steps through the pulse for the currently flashed file, then clears the
// flash: it is one-shot state, and this consumer owns its lifecycle. Keyed on
// ts so re-flashing the same file restarts the animation.
export const useFootprintFlash = (): IFootprintFlashFrame | null => {
  const dispatch = useAppDispatch();
  const flash = useAppSelector((state) => state.fileSelection.flash);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!flash) return undefined;
    setStep(0);
    const timer = setInterval(() => setStep((current) => current + 1), FLASH_STEP_MS);
    return () => clearInterval(timer);
  }, [flash?.ts]);

  useEffect(() => {
    if (flash && step >= FLASH_STEPS.length) dispatch(clearFlash());
  }, [step]);

  if (!flash || step >= FLASH_STEPS.length) return null;
  const [width, opacity] = FLASH_STEPS[step];
  return { kind: flash.kind, id: flash.id, width, opacity };
};

export default useFootprintFlash;
